/**
 * Helpers for Quick Add estimate pricing on unpriced proposal timber lines.
 * Finds nearest priced sizes in the materials database so users can infer $/bf.
 */

import {
    buildTimberMatchKey,
    normalizeMoistureContent,
    type TimberItem,
} from './materialsDatabaseService';
import { gradesEquivalent } from './timberGradeCanonical';

export interface ParsedTimberDimensions {
    height: number;
    width: number;
    length: number;
}

export function parseTimberDimensionParts(dimensions: string): ParsedTimberDimensions | null {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return null;
    const height = parseFloat(match[1]) || 0;
    const width = parseFloat(match[2]) || 0;
    const length = parseFloat(match[3]) || 0;
    if (height <= 0 || width <= 0 || length <= 0) return null;
    return { height, width, length };
}

/** Board feet from H×W×L (inches × inches × feet) ÷ 12 — matches proposal timber list. */
export function boardFeetFromDimensions(dimensions: string): number {
    const parts = parseTimberDimensionParts(dimensions);
    if (!parts) return 0;
    return (parts.height * parts.width * parts.length) / 12;
}

function sortedCrossSection(height: number, width: number): [number, number] {
    return height <= width ? [height, width] : [width, height];
}

/** Lower is closer; cross-section weighted more than length (feet). */
export function dimensionDistance(a: ParsedTimberDimensions, b: ParsedTimberDimensions): number {
    const [ah, aw] = sortedCrossSection(a.height, a.width);
    const [bh, bw] = sortedCrossSection(b.height, b.width);
    const cross = Math.abs(ah - bh) + Math.abs(aw - bw);
    const len = Math.abs(a.length - b.length);
    return cross * 2 + len;
}

export interface NearestTimberMatch {
    dimensions: string;
    costPerBdFt: number;
    boardFeet: number;
    distance: number;
    supplier?: string;
}

export interface QuickAddTimberTarget {
    species: string;
    grade: string;
    dimensions: string;
    moistureContent?: '' | 'green' | 'kiln_dried';
}

function resolveTimberSupplierForDisplay(row: TimberItem): string | undefined {
    if (row.supplier?.trim()) return row.supplier.trim();
    if (row.costPerBdFt <= 0 || !row.supplierPricing?.length) return undefined;
    const priced = row.supplierPricing.find(sp => sp.costPerBdFt === row.costPerBdFt && sp.supplier?.trim());
    return priced?.supplier?.trim() || row.supplierPricing.find(sp => sp.supplier?.trim())?.supplier?.trim();
}

export function canQuickAddTimberEstimate(item: {
    unitCost: number;
    species: string;
    grade: string;
    dimensions: string;
    boardFeet: number;
}): boolean {
    if (item.unitCost > 0) return false;
    if (!item.species?.trim() || !item.grade?.trim() || !item.dimensions?.trim()) return false;
    return item.boardFeet > 0 || boardFeetFromDimensions(item.dimensions) > 0;
}

/**
 * Nearest priced timber rows with the same species, grade, and moisture.
 * Excludes the exact dimension match (that row would already be priced).
 */
export function findNearestPricedTimber(
    database: TimberItem[],
    target: QuickAddTimberTarget,
    limit = 5,
): NearestTimberMatch[] {
    const targetParts = parseTimberDimensionParts(target.dimensions);
    if (!targetParts) return [];

    const targetKey = buildTimberMatchKey(
        target.species,
        target.grade,
        target.dimensions,
        target.moistureContent,
    );
    const mNorm = normalizeMoistureContent(target.moistureContent);

    const candidates: NearestTimberMatch[] = [];
    const seenDims = new Set<string>();

    for (const row of database) {
        if (row.costPerBdFt <= 0) continue;
        if (row.species.trim().toLowerCase() !== target.species.trim().toLowerCase()) continue;
        if (!gradesEquivalent(row.grade, target.grade)) continue;
        if (normalizeMoistureContent(row.moistureContent) !== mNorm) continue;

        const rowKey = buildTimberMatchKey(row.species, row.grade, row.dimensions, row.moistureContent);
        if (rowKey === targetKey) continue;

        const rowParts = parseTimberDimensionParts(row.dimensions);
        if (!rowParts) continue;

        const dimKey = buildTimberMatchKey(row.species, row.grade, row.dimensions, row.moistureContent);
        if (seenDims.has(dimKey)) continue;
        seenDims.add(dimKey);
        candidates.push({
            dimensions: row.dimensions,
            costPerBdFt: row.costPerBdFt,
            boardFeet: row.boardFeet,
            distance: dimensionDistance(targetParts, rowParts),
            supplier: resolveTimberSupplierForDisplay(row),
        });
    }

    candidates.sort((a, b) => a.distance - b.distance);
    return candidates.slice(0, limit);
}

export function piecePriceFromCostPerBdFt(boardFeet: number, costPerBdFt: number): number {
    return Math.round(boardFeet * costPerBdFt * 100) / 100;
}
